
/**
 * Module dependencies.
 */

import { Link } from 'src/components/core/contacts/links';
import { ShowroomModuleProps, ShowroomProps } from 'src/types/showrooms';
import { color, media, units } from 'src/styles/utils';
import { columnTitleStyles } from './footer-theme';
import { normalizeNewLine } from 'src/core/utils/strings';
import { useTranslate } from 'src/core/utils/translator';
import FooterActionLink from './footer-action-link';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import map from 'lodash/map';
import styled from 'styled-components';
import useShowroomModule from 'src/api/app/showrooms/use-showroom-module';
import useShowrooms from 'src/api/app/showrooms/use-showrooms';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(5)};

  ${media.min('md')`
    flex-direction: row;
    gap: ${units(3)};
  `}
`;

/**
 * `Intro` styled component.
 */

const Intro = styled.div`
  flex: none;

  ${media.min('md')`
    width: ${units(28.375)};
  `}
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(2)};

  ${media.min('md')`
    flex: 1 1 0;
    flex-direction: row;
    gap: ${units(2)};
    justify-content: space-between;
    max-width: ${units(70.5)};
  `}
`;

/**
 * `Showroom` styled component.
 */

const Showroom = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(2)};
  min-width: 0;

  ${media.min('md')`
    flex: 1 1 0;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled.h2`
  ${columnTitleStyles}
`;

/**
 * `ShowroomTitle` styled component.
 */

const ShowroomTitle = styled.h3`
  ${columnTitleStyles}
`;

/**
 * `Info` styled component.
 */

const Info = styled.div`
  color: ${color('white')};
  font-size: 14px;
  line-height: 1.5;
  padding: ${units(1)} 0;

  a {
    color: ${color('white')};
  }

  p {
    margin: 0;
  }
`;

/**
 * `Actions` styled component.
 *
 * Stacked, one action per line.
 */

const Actions = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${units(1)};
  padding: ${units(1)} 0;
`;

/**
 * `Showrooms` component.
 *
 * The title and the lead come from the `showroom-module` endpoint, as they did
 * before the revamp; only the showrooms themselves come from `showrooms`.
 */

const Showrooms = (): ReactElement | null => {
  const { translate } = useTranslate();
  const { data: moduleData } = useShowroomModule<ShowroomModuleProps>();
  const { data: showroomsData } = useShowrooms<ShowroomProps[]>();

  if (!showroomsData?.length) {
    return null;
  }

  return (
    <Wrapper>
      <Intro>
        <Title>
          {moduleData?.title ?? translate('footer:showrooms.title')}
        </Title>

        {moduleData?.description && (
          <RawHtml element={Info}>
            {moduleData.description}
          </RawHtml>
        )}
      </Intro>

      <List>
        {map(showroomsData, ({
          mapUrl,
          mobile,
          name,
          phone,
          text,
          virtualTourUrl
        }: ShowroomProps) => (
          <Showroom key={name}>
            <ShowroomTitle>
              {name}
            </ShowroomTitle>

            <div>
              <RawHtml element={Info}>
                {normalizeNewLine(text)}
              </RawHtml>

              {phone && (
                <Info>
                  <Link i18nkey={translate('common:sections.showrooms.phone', { phone })} />
                </Info>
              )}

              {mobile && (
                <Info>
                  <Link i18nkey={translate('common:sections.showrooms.mobile', { mobile })} />
                </Info>
              )}

              {(mapUrl || virtualTourUrl) && (
                <Actions>
                  {mapUrl && (
                    <FooterActionLink href={mapUrl}>
                      {translate('footer:viewMap')}
                    </FooterActionLink>
                  )}

                  {virtualTourUrl && (
                    <FooterActionLink href={virtualTourUrl}>
                      {translate('footer:virtualTour')}
                    </FooterActionLink>
                  )}
                </Actions>
              )}
            </div>
          </Showroom>
        ))}
      </List>
    </Wrapper>
  );
};

/**
 * Export `Showrooms` component.
 */

export default Showrooms;
